import { Box, Chip, Typography } from "@mui/material";
import type { LoadOrderEntry } from "../loadOrder";

const ENTRY_DATE_FORMAT = "M/d";

type LoadOrderCaptionProps = {
	entries: LoadOrderEntry[];
	dataTestId: string;
};

/**
 * The order the files will be loaded in, shown before the operator commits to loading them.
 *
 * The order is derived (shifts by their earliest date, everything else by the order it was listed), and
 * it changes what lands in the database — for a person present in two roster files the first file
 * loaded wins, and shifts loaded out of order report their conflicts against a schedule that already
 * holds the future. Showing it makes that a decision the onboarder can see and re-order, rather than
 * something they discover in the results.
 */
export function LoadOrderCaption({ entries, dataTestId }: LoadOrderCaptionProps) {
	if (entries.length < 2) return null;

	const hasUndated = entries.some((entry) => !entry.date);

	return (
		<Box data-testid={dataTestId}>
			<Typography variant="overline" sx={{ display: "block", color: "text.secondary" }}>
				Load order
			</Typography>
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
				{entries.map((entry, index) => (
					<Chip
						key={entry.id}
						size="small"
						variant="outlined"
						label={`${index + 1}. ${entry.label}${entry.date ? ` (${entry.date.toFormat(ENTRY_DATE_FORMAT)})` : ""}`}
						data-testid={`${dataTestId}-entry-${entry.id}`}
					/>
				))}
			</Box>
			{hasUndated && (
				<Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
					Files without a date keep the order you listed them in. Files are loaded one at a time, in this order.
				</Typography>
			)}
		</Box>
	);
}
